import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
    Button,
    Paper,
    Tooltip
} from '@material-ui/core';
import { FileCopy, Visibility } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'rsuite';
import './ArchiveList.scss';
import _ from 'lodash';
import { dynamicSort } from '@commonClient2/services/sort';
import { useAvailableHeight } from '@commonClient2/hooks';
import { ManagerPage, BidProfileStatus } from '@enums/index';
import { getBidProcessTypeLabel, getScheduleSourceLabel } from '@enums/Helpers/LabellingHelper';
import AppBar from '@manager/AppBar';
import { fetchManagerAppData } from '@manager/store/appData';
import { duplicateBidProfile, loadBidProfileArchive } from '@manager/store/bidProfile';
import FilterSelectionBar from '../BiddingList/FilterSelectionBar';
import {
    formatData,
    hasCreationAccess,
    getRequestPeriodLabel,
    getSubmissionDateLabel,
    searchableBid,
    rebidParentsContainsStatus,
    addRebidParentsStatusToFilter,
} from './ArchiveList.service';
import ArchiveListNameCell from './ArchiveListNameCell';
import ArchiveListTooltipCell from './ArchiveListTooltipCell';

/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

const { Column, HeaderCell, Cell } = Table;

const rowKey = 'id';

/**
 * @param {{
 *  tooltipLabel: string } & import('@material-ui/core').ButtonProps} param0
 */
const ButtonWithTooltip = ({ tooltipLabel, ...rest }) => (
    <Tooltip title={tooltipLabel}>
        <span>
            <Button
                className="tablerow-action-button"
                {...rest}
            >
                {rest.children}
            </Button>
        </span>
    </Tooltip>
);

function ArchiveList() {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'manager.archiveList' });
    const dispatch = useDispatch();

    const [sortValues, setSortValues] = useState({ sortColumn: 'name', sortType: defaultSortType });
    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { managerAppData } = useSelector((state) => state);
    const {
        archivedBidProfiles,
        bidProfileDrafts,
        isAppDataLoaded,
        hyperfinds,
        managerBidSetups,
        pageName,
    } = managerAppData;
    
    /** @type {[string, Function]} */
    const [searchValue, setSearchValue] = useState('');

    /** @type {[number[], Function]} */
    const [statusFilterSelected, setStatusFilterSelected] = useState([BidProfileStatus.Published, BidProfileStatus.Canceled]);

    /** @type {[BidProfile.IBriefBaseProfile[], Function]} */
    // @ts-ignore
    const [filteredData, setFilteredData] = useState(archivedBidProfiles);
    // @ts-ignore
    
    const allStatus = [..._.unionWith(archivedBidProfiles, (a, b) => a.status === b.status).map((x) => x.status)].sort((a, b) => a - b);
    addRebidParentsStatusToFilter(allStatus, archivedBidProfiles);

    const ref = useRef(null);
    const height = useAvailableHeight(ref) || 0;

    /** @param {BidProfile.IBriefBaseProfile} row */
    const handleNameClick = (row) => {
        dispatch(loadBidProfileArchive(row.id, {
            hyperfinds, managerBidSetups, archivedBidProfiles
        }));
    };

    function handleFilter() {
        // @ts-ignore
        let filteredBids = archivedBidProfiles.sort((a, b) => b.lastUpdate - a.lastUpdate);
        if (!searchValue && !statusFilterSelected?.length) {
            setFilteredData(filteredBids);
        }
        
        // filter by search value
        if (searchValue) {
            filteredBids = filteredBids.filter((bid) => searchableBid(bid, t('anytime')).includes(searchValue.trim().toLocaleLowerCase()));
        }
        // filter by status
        if (statusFilterSelected.length) {
            filteredBids = filteredBids.filter((bid) => statusFilterSelected.includes(bid.status)
                || rebidParentsContainsStatus(bid, statusFilterSelected));
        }
        setFilteredData(filteredBids);
    }

    /** @param {BidProfile.IBriefBaseProfile} bid */
    const handleView = (bid) => {
        dispatch(loadBidProfileArchive(bid.id, {
            hyperfinds, managerBidSetups, archivedBidProfiles
        }));
    };

    /**
     * @param {BidProfile.IBriefBaseProfile} bid
     */
    const handleDuplicate = (bid) => {
        dispatch(duplicateBidProfile(
            bid.id,
            {
                bidProfileDrafts,
                hyperfinds,
                managerBidSetups,
            },
        ));
    };

    /**
     * @param {string} dataKey
     * @param {SortType} sortType
     */
    const handleSort = (dataKey, sortType) => {
        /** @type {BidProfile.IBriefBaseProfile[]} */
        let sortedFilteredData = [];
        const dynamicSortOrder = sortType === 'desc' ? '-' : '';
        const order = sortType === 'desc' ? -1 : 1;
        // eslint-disable-next-line max-len
        const nullCheckingDateSort = (/** @type {string} */ propName) => (/** @type {BidProfile.IBriefBaseProfile} */ obj1, /** @type {BidProfile.IBriefBaseProfile} */ obj2) => {
            const a = _.get(obj1, propName, null);
            const b = _.get(obj2, propName, null);
            if (!a && !b) {
                return 0;
            }
            if (!a) {
                return order * -1;
            }
            if (!b) {
                return order * 1;
            }
            const momentA = moment(a);
            const momentB = moment(b);
            // eslint-disable-next-line no-nested-ternary
            return order * ((momentA < momentB) ? -1 : (momentA > momentB) ? 1 : 0);
        };
        const nullCheckingDateRangeSort = (/** @type {string} */ prop1Name, /** @type {string} */ prop2Name) =>
            (/** @type {BidProfile.IBriefBaseProfile} */ obj1, /** @type {BidProfile.IBriefBaseProfile} */ obj2) => {
                const prop1Res = nullCheckingDateSort(prop1Name)(obj1, obj2);
                if (prop1Res !== 0) {
                    return prop1Res;
                }

                return nullCheckingDateSort(prop2Name)(obj1, obj2);
            };
        switch (dataKey) {
            case 'Name':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}name`),
                );
                break;
            case 'Description':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}description`),
                );
                break;
            case 'BidProcess':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}bidProcessType`),
                );
                break;
            case 'ScheduleSource':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}scheduleSource`),
                );
                break;
            case 'People':
                sortedFilteredData = filteredData.sort(
                    dynamicSort(`${dynamicSortOrder}hyperfindLabel`),
                );
                break;
            case 'Request Period':
                sortedFilteredData = filteredData.sort(
                    nullCheckingDateRangeSort('requestPeriodStartDate', 'requestPeriodEndDate'),
                );
                break;
            case 'Start Date':
                sortedFilteredData = filteredData.sort(
                    nullCheckingDateSort('submissionPeriodStartDate'),
                );
                break;
            case 'End Date':
                sortedFilteredData = filteredData.sort(
                    nullCheckingDateSort('submissionPeriodEndDate'),
                );
                break;
            default:
                break;
        }
        setFilteredData(_.cloneDeep(sortedFilteredData));
    };

    /**
     * @param {moment.Moment|null} date
     * @returns {string}
     */
    useEffect(() => {
        if (!isAppDataLoaded) {
            dispatch(fetchManagerAppData());
        }
    }, []);

    useEffect(() => {
        handleSort(sortValues.sortColumn, sortValues.sortType);
    }, [JSON.stringify(sortValues)]);

    useEffect(() => {
        handleFilter();
    }, [searchValue, JSON.stringify(statusFilterSelected), JSON.stringify(archivedBidProfiles)]);

    return (
        <>
            <AppBar
                pageName={pageName}
                pageSelected={ManagerPage.Archives}
            />
            <div className="bid-list-full-page">
                <div className="header-container">
                    <FilterSelectionBar
                        setStatusFilterSelected={setStatusFilterSelected}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        allStatus={allStatus}
                        statusFilterSelected={statusFilterSelected}
                    />
                </div>
                <Paper
                    elevation={2}
                    className="bid-profile-table-container"
                    ref={ref}
                >
                    <Table
                        sortColumn={sortValues.sortColumn}
                        sortType={sortValues.sortType}
                        onSortColumn={(sortColumn, sortType) => setSortValues({ sortColumn, sortType })}
                        height={height}
                        loading={!isAppDataLoaded}
                        data={formatData(filteredData)}
                        id="bid-profile-table"
                        className="table-section"
                        rowClassName="bid-profile-table-row"
                        rowKey={rowKey}
                        isTree
                        shouldUpdateScroll={false}
                    >
                        <Column width={32}>
                            <HeaderCell> </HeaderCell>
                            <Cell />
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                            key="name"
                        >
                            <HeaderCell>{t('tableHeaders.name')}</HeaderCell>
                            <ArchiveListNameCell
                                dataKey="Name"
                                handleNameClick={handleNameClick}
                                iconTooltipLabel={t('tooltip.parent')}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.description')}</HeaderCell>
                            <ArchiveListTooltipCell
                                dataKey="Description"
                                getValue={(rowData) => rowData.description}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.bidProcess')}</HeaderCell>
                            <ArchiveListTooltipCell
                                dataKey="BidProcess"
                                getValue={(rowData) => getBidProcessTypeLabel(rowData.bidProcessType)}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            minWidth={140}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.scheduleSource')}</HeaderCell>
                            <ArchiveListTooltipCell
                                dataKey="ScheduleSource"
                                getValue={(rowData) => getScheduleSourceLabel(rowData.scheduleSource)}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.people')}</HeaderCell>
                            <ArchiveListTooltipCell
                                dataKey="People"
                                getValue={(rowData) => rowData.hyperfindLabel}
                            />
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.requestPeriod')}</HeaderCell>
                            <Cell
                                dataKey="Request Period"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) =>
                                    getRequestPeriodLabel(rowData, t('anytime'))}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.startDateTime')}</HeaderCell>
                            <Cell
                                dataKey="Start Date"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) =>
                                    getSubmissionDateLabel(rowData.submissionPeriodStartDate)}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={2}
                            minWidth={150}
                            sortable
                        >
                            <HeaderCell>{t('tableHeaders.endDateTime')}</HeaderCell>
                            <Cell
                                dataKey="End Date"
                                style={{ cursor: 'default' }}
                            >
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) =>
                                    getSubmissionDateLabel(rowData.submissionPeriodEndDate)}
                            </Cell>
                        </Column>
                        <Column
                            minWidth={260}
                            flexGrow={4}
                        >
                            <HeaderCell> </HeaderCell>
                            <Cell>
                                {(/** @type {import('rsuite/esm/Table').RowDataType<BidProfile.IBriefBaseProfile>} */rowData) => ((
                                    <div
                                        className="tablerow-action-button-container"
                                    >
                                        <ButtonWithTooltip
                                            onClick={() => handleView(rowData)}
                                            tooltipLabel={t('rowActions.view')}
                                        >
                                            <Visibility fontSize="inherit" />
                                        </ButtonWithTooltip>
                                        {
                                            hasCreationAccess(rowData) && (
                                                <ButtonWithTooltip
                                                    onClick={() => handleDuplicate(rowData)}
                                                    disabled={!hasCreationAccess(rowData)}
                                                    tooltipLabel={t('rowActions.duplicate')}
                                                >
                                                    <FileCopy fontSize="inherit" />
                                                </ButtonWithTooltip>
                                            )
                                        }
                                    </div>
                                )
                                )}
                            </Cell>
                        </Column>
                    </Table>
                </Paper>
            </div>

        </>
    );
}

export default ArchiveList;
